import React, { useRef, useEffect } from 'react';
import VoiceCallTodoCard from './VoiceCallTodoCard';
import VoiceCallEditorCard from './VoiceCallEditorCard';
import VoiceCallProjectCard from './VoiceCallProjectCard';
import './voice-call-card-stack.scss';

export interface VoiceCallTodoItem {
  type: 'todo';
  id: string;
  description: string;
  actionKey: string;
  actionLabel: string;
  status: 'pending' | 'creating' | 'created';
  spendDescription?: string;
  estimatedSpend?: string;
  spendCurrency?: string;
}

/**
 * Inline collaborative markdown editor card surfaced during a voice call.
 * The agent opens one via the `open_editor` client tool and mutates its
 * `content` with `patch_editor`; the user types directly into the card and
 * hits the save button. Kept in the same stack as todo cards on purpose —
 * visually consistent and the stack auto-scrolls to reveal new cards.
 */
export interface VoiceCallEditorItem {
  type: 'editor';
  id: string;
  editorType: 'document' | 'email_draft';
  title: string;
  content: string;
  status: 'editing' | 'saving' | 'saved' | 'error';
  saveError?: string;
  savedArtifactId?: string;
  savedDraftId?: string;
  gmailUrl?: string;
  // email_draft-only fields
  oauthId?: number;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
}

export interface VoiceCallProjectItem {
  type: 'project';
  id: string;
  projectName: string;
  direction: string;
  status: 'pending' | 'creating' | 'created';
}

// Discriminated union — extend with other card types in the future
export type VoiceCallCard =
  | VoiceCallTodoItem
  | VoiceCallEditorItem
  | VoiceCallProjectItem;

export interface OauthAccountOption {
  id: number;
  email: string;
}

interface VoiceCallCardStackProps {
  cards: VoiceCallCard[];
  agentNickname?: string;
  onCreateTodo: (id: string) => void;
  onRemoveTodo: (id: string) => void;
  /** Called when the user types in an editor. Parent is responsible for debouncing push-back to the agent. */
  onEditorContentChange: (id: string, content: string) => void;
  /** Called when the user updates a header field (title, to/cc/bcc/subject, oauthId). */
  onEditorFieldChange: (
    id: string,
    field: 'title' | 'to' | 'cc' | 'bcc' | 'subject' | 'oauthId',
    value: string | number,
  ) => void;
  onEditorRemove: (id: string) => void;
  onSaveDocument: (id: string) => void;
  onSaveEmailDraft: (id: string) => void;
  onStartProject: (id: string) => void;
  onRemoveProject: (id: string) => void;
  /** OAuth email accounts available for the draft sender picker. */
  oauthAccounts?: OauthAccountOption[];
}

const VoiceCallCardStack: React.FC<VoiceCallCardStackProps> = ({
  cards,
  agentNickname,
  onCreateTodo,
  onRemoveTodo,
  onEditorContentChange,
  onEditorFieldChange,
  onEditorRemove,
  onSaveDocument,
  onSaveEmailDraft,
  onStartProject,
  onRemoveProject,
  oauthAccounts,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new cards are added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [cards.length]);

  if (cards.length === 0) return null;

  // Editor cards get a wider stack on desktop so they don't feel cramped.
  // Documents get the most room (full modal-width); email drafts get a
  // mid-width treatment. Todo cards inside the same stack keep their own
  // intrinsic sizing via `width: 100%` so they just stretch to match.
  // Document takes priority if both happen to be present.
  const hasDocumentEditor = cards.some(
    (c) => c.type === 'editor' && c.editorType === 'document',
  );
  const hasEmailEditor = cards.some(
    (c) => c.type === 'editor' && c.editorType === 'email_draft',
  );
  const stackVariantClass = hasDocumentEditor
    ? ' voice-call-card-stack--has-document-editor'
    : hasEmailEditor
      ? ' voice-call-card-stack--has-email-editor'
      : '';
  const stackClassName = `voice-call-card-stack${stackVariantClass}`;

  return (
    <div className={stackClassName}>
      <div className='voice-call-card-stack-scroll' ref={scrollRef}>
        {cards.map((card) => {
          if (card.type === 'todo') {
            return (
              <VoiceCallTodoCard
                key={card.id}
                id={card.id}
                description={card.description}
                actionKey={card.actionKey}
                actionLabel={card.actionLabel}
                status={card.status}
                spendDescription={card.spendDescription}
                estimatedSpend={card.estimatedSpend}
                spendCurrency={card.spendCurrency}
                agentNickname={agentNickname}
                onCreateTodo={onCreateTodo}
                onRemove={onRemoveTodo}
              />
            );
          }
          if (card.type === 'editor') {
            return (
              <VoiceCallEditorCard
                key={card.id}
                card={card}
                oauthAccounts={oauthAccounts}
                onContentChange={onEditorContentChange}
                onFieldChange={onEditorFieldChange}
                onRemove={onEditorRemove}
                onSaveDocument={onSaveDocument}
                onSaveEmailDraft={onSaveEmailDraft}
              />
            );
          }
          if (card.type === 'project') {
            return (
              <VoiceCallProjectCard
                key={card.id}
                id={card.id}
                projectName={card.projectName}
                direction={card.direction}
                status={card.status}
                agentNickname={agentNickname}
                onStartProject={onStartProject}
                onRemove={onRemoveProject}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default VoiceCallCardStack;
